import React, { useState } from "react";
import {
  Calculator,
  TrendingUp,
  Calendar,
  Percent,
  ArrowRight,
  CheckCircle,
} from "lucide-react";

// Mock Link component since we don't have react-router-dom
const Link = ({ to, children, className }) => (
  <a href={to} className={className}>
    {children}
  </a>
);

// DataTable component
const DataTable = ({ columns, data, emptyMessage }) => {
  if (!data || data.length === 0) {
    return (
      <div className="empty-state">
        <Calculator className="empty-icon" />
        <p className="empty-message">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="data-table-container">
      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key} className="table-header">
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => (
              <tr key={index} className="table-row">
                {columns.map((col) => (
                  <td key={col.key} className="table-cell">
                    {col.render ? col.render(row) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const FDCalculator = () => {
  const [principal, setPrincipal] = useState("");
  const [rate, setRate] = useState("");
  const [durationValue, setDurationValue] = useState("");
  const [durationUnit, setDurationUnit] = useState("months");
  const [totalInterest, setTotalInterest] = useState(null);
  const [tableData, setTableData] = useState([]);

  const handleCalculate = () => {
    const P = parseFloat(principal);
    const R = parseFloat(rate);
    let months = parseFloat(durationValue) || 0;
    if (durationUnit === "years") {
      months = months * 12;
    }
    if (!P || !R || !months) return;

    const monthlyRate = R / 12 / 100;
    const data = [];
    for (let m = 1; m <= months; m++) {
      const interestEarned = P * monthlyRate * m;
      const totalAmt = P + interestEarned;
      data.push({
        month: m,
        interest: interestEarned.toFixed(2),
        total: totalAmt.toFixed(2),
      });
    }
    const totalInt = P * (R / 100) * (months / 12);
    setTotalInterest(totalInt.toFixed(2));
    setTableData(data);
  };

  const columns = [
    {
      key: "month",
      header: "Month",
      render: (row) => (
        <div className="month-cell">
          <Calendar className="month-icon" />
          Month {row.month}
        </div>
      ),
    },
    {
      key: "interest",
      header: "Interest Earned",
      render: (row) => (
        <div className="currency-cell">
          <TrendingUp className="currency-icon" />₹{row.interest}
        </div>
      ),
    },
    {
      key: "total",
      header: "Total Amount",
      render: (row) => (
        <div className="currency-cell total-amount">₹{row.total}</div>
      ),
    },
  ];

  return (
    <div className="calculator-container">
      <div className="calculator-card">
        {/* Header Section */}
        <div className="calculator-header">
          <div className="header-icon">
            <Calculator className="header-icon-svg" />
          </div>
          <div className="header-text">
            <h2 className="calculator-title">FD Calculator</h2>
            <p className="calculator-subtitle">
              Calculate your Fixed Deposit returns with ease
            </p>
          </div>
        </div>

        {/* Input Form */}
        <div className="calculator-form">
          <div className="input-grid">
            <div className="input-group">
              <label className="input-label">
                <TrendingUp className="label-icon" />
                Amount Invested (₹)
              </label>
              <input
                type="number"
                value={principal}
                onChange={(e) => setPrincipal(e.target.value)}
                className="form-input"
                placeholder="e.g. 100000"
              />
            </div>

            <div className="input-group">
              <label className="input-label">
                <Percent className="label-icon" />
                Return % per annum
              </label>
              <input
                type="number"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                className="form-input"
                placeholder="e.g. 6.5"
              />
            </div>

            <div className="input-group">
              <label className="input-label">
                <Calendar className="label-icon" />
                Duration Value
              </label>
              <input
                type="number"
                value={durationValue}
                onChange={(e) => setDurationValue(e.target.value)}
                className="form-input"
                placeholder="e.g. 12"
              />
            </div>

            <div className="input-group">
              <label className="input-label">Duration Unit</label>
              <select
                value={durationUnit}
                onChange={(e) => setDurationUnit(e.target.value)}
                className="form-select"
              >
                <option value="months">Months</option>
                <option value="years">Years</option>
              </select>
            </div>
          </div>

          <button onClick={handleCalculate} className="calculate-button">
            <Calculator className="button-icon" />
            Calculate Returns
          </button>
        </div>

        {/* Results Section */}
        {totalInterest !== null && (
          <div className="results-section">
            <div className="result-card">
              <div className="result-content">
                <div className="result-icon">
                  <TrendingUp className="result-icon-svg" />
                </div>
                <div className="result-text">
                  <span className="result-label">Total Interest Earned</span>
                  <span className="result-value">₹{totalInterest}</span>
                </div>
              </div>
              <Link to="/apply" className="apply-button">
                <CheckCircle className="apply-icon" />
                Apply Now
                <ArrowRight className="apply-arrow" />
              </Link>
            </div>
          </div>
        )}

        {/* Data Table */}
        <div className="table-section">
          <DataTable
            columns={columns}
            data={tableData}
            emptyMessage="No results — enter inputs and click Calculate"
          />
        </div>
      </div>

      <style jsx>{`
        .calculator-container {
          min-height: 100vh;
          background: linear-gradient(
            135deg,
            #f0f9ff 0%,
            #ffffff 50%,
            #faf5ff 100%
          );
          padding: 2rem 1rem;
          display: flex;
          justify-content: center;
          align-items: flex-start;
        }

        .calculator-card {
          background: white;
          border-radius: 1.5rem;
          padding: 2.5rem;
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
          border: 1px solid #f3f4f6;
          max-width: 1000px;
          width: 100%;
          position: relative;
        }

        .calculator-card::before {
          content: "";
          position: absolute;
          top: -1rem;
          right: -1rem;
          width: 5rem;
          height: 5rem;
          background: linear-gradient(135deg, #fbbf24, #f59e0b);
          border-radius: 50%;
          opacity: 0.2;
          animation: pulse 2s infinite;
        }

        .calculator-card::after {
          content: "";
          position: absolute;
          bottom: -1rem;
          left: -1rem;
          width: 4rem;
          height: 4rem;
          background: linear-gradient(135deg, #a855f7, #ec4899);
          border-radius: 50%;
          opacity: 0.2;
          animation: pulse 2s infinite;
          animation-delay: 1s;
        }

        @keyframes pulse {
          0%,
          100% {
            transform: scale(1);
            opacity: 0.2;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.3;
          }
        }

        .calculator-header {
          display: flex;
          align-items: center;
          gap: 1.5rem;
          margin-bottom: 2.5rem;
          padding-bottom: 2rem;
          border-bottom: 2px solid #f3f4f6;
        }

        .header-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 4rem;
          height: 4rem;
          background: linear-gradient(135deg, #2563eb, #9333ea);
          color: white;
          border-radius: 1rem;
        }

        .header-icon-svg {
          width: 2rem;
          height: 2rem;
        }

        .header-text {
          flex: 1;
        }

        .calculator-title {
          font-size: 2rem;
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 0.5rem;
        }

        .calculator-subtitle {
          color: #6b7280;
          font-size: 1rem;
        }

        .calculator-form {
          margin-bottom: 2.5rem;
        }

        .input-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        @media (min-width: 768px) {
          .input-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        .input-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .input-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
          color: #374151;
          font-size: 0.875rem;
        }

        .label-icon {
          width: 1rem;
          height: 1rem;
          color: #2563eb;
        }

        .form-input,
        .form-select {
          padding: 0.875rem 1rem;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-size: 1rem;
          transition: all 0.3s ease;
          background: white;
        }

        .form-input:focus,
        .form-select:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }

        .calculate-button {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          width: 100%;
          padding: 1rem 2rem;
          background: linear-gradient(135deg, #2563eb, #9333ea);
          color: white;
          font-weight: 600;
          border-radius: 0.75rem;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 1rem;
          box-shadow: 0 10px 25px rgba(37, 99, 235, 0.3);
        }

        .calculate-button:hover {
          background: linear-gradient(135deg, #1d4ed8, #7c3aed);
          transform: translateY(-2px);
          box-shadow: 0 15px 35px rgba(37, 99, 235, 0.4);
        }

        .button-icon {
          width: 1.25rem;
          height: 1.25rem;
        }

        .results-section {
          margin-bottom: 2.5rem;
        }

        .result-card {
          background: linear-gradient(135deg, #dbeafe, #ede9fe);
          border-radius: 1rem;
          padding: 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1.5rem;
          border: 1px solid #e5e7eb;
        }

        @media (max-width: 768px) {
          .result-card {
            flex-direction: column;
            text-align: center;
          }
        }

        .result-content {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .result-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 3rem;
          height: 3rem;
          background: linear-gradient(135deg, #10b981, #2563eb);
          color: white;
          border-radius: 0.75rem;
        }

        .result-icon-svg {
          width: 1.5rem;
          height: 1.5rem;
        }

        .result-text {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .result-label {
          color: #6b7280;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .result-value {
          font-size: 1.5rem;
          font-weight: 700;
          background: linear-gradient(135deg, #10b981, #2563eb);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: #10b981;
        }

        .apply-button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
          font-weight: 600;
          border-radius: 0.5rem;
          text-decoration: none;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        }

        .apply-button:hover {
          background: linear-gradient(135deg, #059669, #047857);
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(16, 185, 129, 0.4);
        }

        .apply-icon,
        .apply-arrow {
          width: 1rem;
          height: 1rem;
        }

        .apply-button:hover .apply-arrow {
          transform: translateX(4px);
        }

        .table-section {
          position: relative;
          z-index: 1;
        }

        .data-table-container {
          background: white;
          border-radius: 1rem;
          overflow: hidden;
          border: 1px solid #e5e7eb;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
        }

        .table-wrapper {
          overflow-x: auto;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
        }

        .table-header {
          background: linear-gradient(135deg, #f8fafc, #f1f5f9);
          padding: 1rem;
          text-align: left;
          font-weight: 600;
          color: #374151;
          border-bottom: 2px solid #e5e7eb;
        }

        .table-row {
          transition: background-color 0.2s ease;
        }

        .table-row:hover {
          background: #f9fafb;
        }

        .table-cell {
          padding: 1rem;
          border-bottom: 1px solid #f3f4f6;
        }

        .month-cell,
        .currency-cell {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .month-icon,
        .currency-icon {
          width: 1rem;
          height: 1rem;
          color: #6b7280;
        }

        .total-amount {
          font-weight: 600;
          color: #2563eb;
        }

        .empty-state {
          text-align: center;
          padding: 3rem 2rem;
          color: #6b7280;
        }

        .empty-icon {
          width: 3rem;
          height: 3rem;
          color: #d1d5db;
          margin: 0 auto 1rem;
        }

        .empty-message {
          font-size: 1rem;
        }

        @media (max-width: 768px) {
          .calculator-container {
            padding: 1rem;
          }

          .calculator-card {
            padding: 1.5rem;
          }

          .calculator-title {
            font-size: 1.5rem;
          }

          .calculator-header {
            flex-direction: column;
            text-align: center;
            gap: 1rem;
          }

          .input-grid {
            grid-template-columns: 1fr;
          }

          .result-value {
            font-size: 1.25rem;
          }
        }
      `}</style>
    </div>
  );
};

export default FDCalculator;
